import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DesignTokens } from "melco-ui";
import {
  StatisticsField,
  StatisticsConfig,
} from "../../../../hooks/reporting/statistics/useStatistics";
import { useStatisticsChart } from "../../../../hooks/reporting/statistics/useStatisticsChart";
import { ReportingMelcoModelsGenericList1MelcoMachineStatisticsModelsStatisticListItem } from "melco-shared-logic";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import LoadingDefaultSpinIndicator from "../../../loading/LoadingDefaultSpinIndicator";

const { colors } = DesignTokens;

const COLORS_CHART_REGULAR = "#8c8c8c";
const COLORS_CHART_HOVER_BACKGROUND = colors.gray.light;

type StatisticsBarChartProps = {
  field: StatisticsField;
  data?: ReportingMelcoModelsGenericList1MelcoMachineStatisticsModelsStatisticListItem;
  dataConfig?: StatisticsConfig;
  isLoading?: boolean;
};

const LoaderDiv = styled.div`
  position: relative;
  top: 30%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

// Styled-component div
const StyledDiv = styled.div`
  position: relative;
  height: 330px;
  overflow-x: auto;
  overflow-y: hidden;
  /* For WebKit browsers */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  ::-webkit-scrollbar-thumb {
    background-color: darkgrey;
    border-radius: 10px;
  }
  ::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  /* For Firefox */
  .scrollable {
    scrollbar-width: thin;
    scrollbar-color: darkgrey #f1f1f1;
  }
`;

export const StatisticsBarChart: React.FC<StatisticsBarChartProps> = ({
  field,
  data,
  dataConfig,
  isLoading,
}) => {
  const { t } = useTranslation();

  const {
    tickNumberFormatter,
    toolTipNumberFormatter,
    chartWidth,
    wrapperRef,
    maxBarSize,
  } = useStatisticsChart(data?.list ?? []);

  return (
    <StyledDiv ref={wrapperRef}>
      {isLoading && (
        <LoaderDiv>
          <LoadingDefaultSpinIndicator
            isShown={isLoading}
          ></LoadingDefaultSpinIndicator>
        </LoaderDiv>
      )}
      <ResponsiveContainer minWidth="100%" width={chartWidth} height={300}>
        <BarChart data={data?.list ?? []} layout="horizontal">
          <Tooltip
            formatter={toolTipNumberFormatter}
            cursor={{ fill: COLORS_CHART_HOVER_BACKGROUND }}
          />
          <CartesianGrid strokeDasharray="3 3" />
          {(data?.list ?? [])?.length > 0 ? (
            <Bar
              dataKey={field}
              fill={COLORS_CHART_REGULAR}
              maxBarSize={maxBarSize}
            />
          ) : (
            !isLoading && (
              <text
                x="50%"
                y="40%"
                textAnchor="middle"
                fill={COLORS_CHART_REGULAR}
              >
                {t("reporting.statistics.charts.no_data_available")}
              </text>
            )
          )}
          {/* <Bar
            dataKey={field}
            fill={COLORS_CHART_REGULAR}
            maxBarSize={maxBarSize}
          /> */}
          <YAxis dataKey={field} tickFormatter={tickNumberFormatter} />
          <XAxis
            dataKey={dataConfig?.grouped ? "group_name" : "machine_name"}
            interval={0}
            angle={-45}
            textAnchor="end"
            height={80}
          />
        </BarChart>
      </ResponsiveContainer>
    </StyledDiv>
  );
};
